import { z } from 'zod';

const baseMetaField = z.object({
  name: z.string(),
  type: z.string(),
  dataType: z.string().optional(),
  dataSpecialization: z.string().optional(),
  label: z.string(),

  multiValue: z.boolean().optional(),
  required: z.boolean().optional(),
  optional: z.boolean().optional(),
  readonly: z.boolean().optional(),
  defaultValue: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
    .optional(),
  inputType: z.string().optional(),
  confidential: z.boolean().optional(),
  hideFromSearch: z.boolean().optional(),
});

export const Option = z.object({
  value: z.string().or(z.number()),
  label: z.string(),

  // Extra fields we add for Specialty options:
  rawName: z.string().optional(),
  categoryName: z.string().optional(),
  categoryId: z.number().optional(),
});
export type Option = z.infer<typeof Option>;

export const Options = Option.array();
export type Options = z.infer<typeof Options>;

const baseReferenceFieldSchema = baseMetaField.extend({
  type: z.enum(['TO_MANY', 'TO_ONE']),
  optionsType: z.string(),
  optionsUrl: z.string(),
  options: Options.optional(),
});

export type ToManyField = z.infer<typeof baseReferenceFieldSchema> & {
  type: 'TO_MANY';
  associatedEntity: FullMeta;
};
export const ToManyField: z.ZodType<ToManyField> = baseReferenceFieldSchema.extend({
  type: z.literal('TO_MANY'),
  associatedEntity: z.lazy(() => FullMeta),
});
export type ToOneField = z.infer<typeof baseReferenceFieldSchema> & {
  type: 'TO_ONE';
  associatedEntity: FullMeta;
};
export const ToOneField: z.ZodType<ToOneField> = baseReferenceFieldSchema.extend({
  type: z.literal('TO_ONE'),
  associatedEntity: z.lazy(() => FullMeta),
});
export type ReferenceField = ToOneField | ToManyField;
export const ReferenceField = z.union([ToOneField, ToManyField]);

// Specific field types
export const IdField = baseMetaField.extend({
  type: z.literal('ID'),
  dataType: z.string(),
});
export type IdField = z.infer<typeof IdField>;

// Various scalar fields
export const ScalarField = baseMetaField.extend({
  type: z.literal('SCALAR'),
  dataType: z.string(),
  dataSpecialization: z.string().optional(),
  optionsType: z.string().optional(),
  optionsUrl: z.string().optional(),

  // String fields only
  maxLength: z.number().optional(),
  options: Options.optional(),
});
export type ScalarField = z.infer<typeof ScalarField>;

export type CompositeField = z.infer<typeof baseMetaField> & {
  type: 'COMPOSITE';
  dataType: string;
  fields: Field[];
};
export const CompositeField: z.ZodType<CompositeField> = baseMetaField.extend({
  type: z.literal('COMPOSITE'),
  dataType: z.string(),
  fields: z.lazy(() => Field.array()),
});

const fieldTypes = [IdField, ToOneField, ToManyField, ScalarField, CompositeField] as const;
export const Field = z.union(fieldTypes);
export type Field = z.infer<typeof Field>;

export const FullMeta = z.object({
  entity: z.string(),
  entityMetaUrl: z.string().optional(),
  label: z.string(),
  dateLastModified: z.union([z.string(), z.number()]).optional(),
  fields: Field.array(),
});
export type FullMeta = z.infer<typeof FullMeta>;

// Type guards to narrow field types
const isFieldType = <T extends (typeof fieldTypes)[number]>(
  schema: T,
  field: Field
): field is z.infer<T> => {
  if (schema === ToOneField) return field.type === 'TO_ONE';
  if (schema === ToManyField) return field.type === 'TO_MANY';
  if (schema === CompositeField) return field.type === 'COMPOSITE';
  if (schema === ScalarField) return field.type === 'SCALAR';
  if (schema === IdField) return field.type === 'ID';
  return false;
};
export const isToManyField = (field: Field): field is ToManyField => {
  return field.type === 'TO_MANY';
};

export const isCompositeField = (field: Field): field is CompositeField => {
  return field.type === 'COMPOSITE';
};

/** Whether a field's value is an array on the wire (a TO_MANY or a multiValue scalar), not whether the UI accepts several. */
export const isArrayField = (field: Pick<Field, 'type' | 'multiValue'>): boolean => {
  return field.type === 'TO_MANY' || field.multiValue === true;
};

/** A TO_MANY that Bullhorn's UI shows as single-select. */
export const isSingleSelectAssociation = (field: Pick<Field, 'type' | 'multiValue'>): boolean =>
  field.type === 'TO_MANY' && field.multiValue !== true;

/** A field naming a list of values that meta did not inline, so the list needs its own fetch. */
export const isUnfetchedPicklist = (field: Field): boolean => {
  return 'optionsType' in field && !!field.optionsType && !field.options?.length;
};

// Bullhorn comma-joins multiValue fields and re-splits on every comma, so an embedded comma
// fragments the value on the next read; swap it for a separator that survives the round-trip.
export const sanitizeMultiValueString = (value: string): string =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' - ');

// Loose key so LLM run-to-run phrasing variants dedupe to one entry instead of accumulating.
export const multiValueDedupeKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
